import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CreditTransaction, CreditTransactionType } from '@/types/ads'

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

interface CreditState {
  balance: number
  transactions: CreditTransaction[]
  initialized: boolean
  /** Tracks which ad contacts have been unlocked: 'hire:<id>' | 'job:<id>' */
  viewedAds: string[]
  /** Store contact info for viewed ads: keyed by 'hire:<id>' or 'job:<id>' */
  viewedContacts: Record<string, { email: string; phone?: string }>
  /** Give 250 welcome credits on first sign-in; idempotent. */
  initialize: () => void
  /** Deduct credits. Returns false if balance is insufficient. */
  spend: (amount: number, type: CreditTransactionType, description: string) => boolean
  /** Add credits to the balance. */
  credit: (amount: number, type: CreditTransactionType, description: string) => void
  /** Sync balance from backend. */
  setBalance: (balance: number) => void
  /** Sync transactions from backend. */
  setTransactions: (transactions: CreditTransaction[]) => void
  hasViewed: (key: string) => boolean
  markViewed: (key: string) => void
  /** Save contact info for a viewed ad/job */
  saveViewedContact: (key: string, email: string, phone?: string) => void
  /** Get contact info for a viewed ad/job */
  getViewedContact: (key: string) => { email: string; phone?: string } | undefined
}

export const useCreditStore = create<CreditState>()(
  persist(
    (set, get) => ({
      balance: 0,
      transactions: [],
      initialized: false,
      viewedAds: [],
      viewedContacts: {},

      initialize: () => {
        if (get().initialized) return
        const tx: CreditTransaction = {
          id: genId(),
          type: 'welcome_bonus',
          amount: 250,
          description: 'Welcome bonus — 250 free credits on first sign-in',
          createdAt: new Date().toISOString(),
        }
        set({ balance: 250, initialized: true, transactions: [tx] })
      },

      spend: (amount, type, description) => {
        if (get().balance < amount) return false
        const tx: CreditTransaction = {
          id: genId(),
          type,
          amount: -amount,
          description,
          createdAt: new Date().toISOString(),
        }
        set((s) => ({ balance: s.balance - amount, transactions: [tx, ...s.transactions] }))
        return true
      },

      credit: (amount, type, description) => {
        const tx: CreditTransaction = {
          id: genId(),
          type,
          amount,
          description,
          createdAt: new Date().toISOString(),
        }
        set((s) => ({ balance: s.balance + amount, transactions: [tx, ...s.transactions] }))
      },

      setBalance: (balance) => set({ balance }),

      setTransactions: (transactions) => set({ transactions }),

      hasViewed: (key) => get().viewedAds.includes(key),
      markViewed: (key) =>
        set((s) => ({
          viewedAds: s.viewedAds.includes(key) ? s.viewedAds : [...s.viewedAds, key],
        })),

      saveViewedContact: (key, email, phone) =>
        set((s) => ({
          viewedContacts: {
            ...s.viewedContacts,
            [key]: { email, phone },
          },
        })),

      getViewedContact: (key) => get().viewedContacts[key],
    }),
    { name: 'credits-storage' },
  ),
)
